/**
 * Dev preview session fixtures.
 *
 * Centralizes the seeded cohort, participant, and session data used by the
 * extracted dev-mode domains. These fixtures intentionally stay static so the
 * domain modules can layer scenario transforms and persistence on top.
 */

import { CoachingSessionStatus } from '../../../enums/coaching.enum';
import { GroupStatus, SessionStageType, UserRoleType, UserStatus } from '../../../enums/enums';
import { type ICoachingSessionFullDTO } from '../../../models/CoachingSessionFullDTO';
import { getDateOffset, getDateOffsetRelative } from '../support/date-utils';
import { baseInternalUser, buildSessionUser, coachCaseyProfile } from './profile';

// --- Users ---

export const leaderUser = buildSessionUser({
  id: 'dev-leader-1',
  name: 'Jordan Lee',
  email: 'jordan.lee@besthuman.test',
  type: UserRoleType.Mentor,
  title: 'VP of People',
  company: 'Northwind Labs',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/jordan-lee-preview/',
  isCoach: true,
  isCoachee: false
});

export const coLeaderUser = buildSessionUser({
  ...coachCaseyProfile,
  isCoach: true,
  isCoachee: false
});

export const currentSessionUser = buildSessionUser({
  id: baseInternalUser.id,
  name: baseInternalUser.name,
  email: baseInternalUser.email,
  email2: baseInternalUser.email2 ?? '',
  type: baseInternalUser.type ?? UserRoleType.Mentee,
  clientId: baseInternalUser.clientId ?? 'client-dev-1',
  avatar: baseInternalUser.avatar ?? '',
  linkedin: baseInternalUser.linkedin ?? '',
  title: baseInternalUser.title ?? '',
  company: baseInternalUser.company ?? '',
  city: baseInternalUser.city ?? '',
  country: baseInternalUser.country ?? '',
  timezone: baseInternalUser.timezone ?? '',
  status: baseInternalUser.status ?? UserStatus.Active,
  careerGoals: baseInternalUser.careerGoals
});

export const cohortPeer = buildSessionUser({
  id: 'dev-peer-1',
  name: 'Morgan Mills',
  email: 'morgan.mills@besthuman.test',
  title: 'Head of Operations',
  company: 'Northwind Labs',
  avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/morgan-mills-preview/',
  city: 'Seattle',
  country: 'United States',
  timezone: 'America/Los_Angeles'
});

export const cohortPeerTwo = buildSessionUser({
  id: 'dev-peer-2',
  name: 'Avery Stone',
  email: 'avery.stone@besthuman.test',
  title: 'Director of Strategy',
  company: 'Northwind Labs',
  linkedin: 'https://www.linkedin.com/in/avery-stone-preview/',
  city: 'Portland',
  country: 'United States',
  timezone: 'America/Los_Angeles'
});

export const cohortPeerThree = buildSessionUser({
  id: 'dev-peer-3',
  name: 'Jamie Reed',
  email: 'jamie.reed@besthuman.test',
  title: 'Engineering Manager',
  company: 'Northwind Labs',
  avatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/jamie-reed-preview/',
  city: 'Boston',
  country: 'United States',
  timezone: 'America/New_York'
});

export const cohortPeerFour = buildSessionUser({
  id: 'dev-peer-4',
  name: 'Sam Kim',
  email: 'sam.kim@besthuman.test',
  title: 'Product Lead',
  company: 'Northwind Labs',
  linkedin: 'https://www.linkedin.com/in/sam-kim-preview/',
  city: 'Toronto',
  country: 'Canada',
  timezone: 'America/Toronto'
});

export const coachCohortPeer = buildSessionUser({
  id: 'dev-coach-peer-1',
  name: 'Priya Shah',
  email: 'priya.shah@besthuman.test',
  title: 'Senior Leadership Coach',
  company: 'BestHuman',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/priya-shah-preview/',
  city: 'San Francisco',
  country: 'United States',
  timezone: 'America/Los_Angeles',
  isCoach: true,
  isCoachee: false
});

export const coachCohortPeerTwo = buildSessionUser({
  id: 'dev-coach-peer-2',
  name: 'Elliot Park',
  email: 'elliot.park@besthuman.test',
  title: 'Facilitation Lead',
  company: 'BestHuman',
  linkedin: 'https://www.linkedin.com/in/elliot-park-preview/',
  city: 'Chicago',
  country: 'United States',
  timezone: 'America/Chicago',
  isCoach: true,
  isCoachee: false
});

export const coachLedParticipant = buildSessionUser({
  id: 'dev-coach-led-participant-1',
  name: 'Riley Quinn',
  email: 'riley.quinn@besthuman.test',
  title: 'Director of Customer Success',
  company: 'Northwind Labs',
  avatar: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/riley-quinn-preview/',
  city: 'Austin',
  country: 'United States',
  timezone: 'America/Chicago'
});

export const coachLedParticipantTwo = buildSessionUser({
  id: 'dev-coach-led-participant-2',
  name: 'Taylor Brooks',
  email: 'taylor.brooks@besthuman.test',
  title: 'Head of Product Marketing',
  company: 'Northwind Labs',
  linkedin: 'https://www.linkedin.com/in/taylor-brooks-preview/',
  city: 'Denver',
  country: 'United States',
  timezone: 'America/Denver'
});

export const veteranValUser = buildSessionUser({
  id: 'dev-veteran-val',
  name: 'Val Vance',
  email: 'val.vance@besthuman.test',
  type: UserRoleType.Mentee,
  title: 'Senior Portfolio Manager',
  company: 'Global Finance Corp',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/val-vance-preview/',
  city: 'New York',
  country: 'United States',
  timezone: 'America/New_York',
  careerGoals: [{ date: getDateOffset(-365, 9), goal: 'Transition into strategic executive leadership.' }]
});

// --- Groups ---

export const baseGroups = [
  {
    id: 'dev-group-1',
    description: 'Dev Preview Cohort',
    status: GroupStatus.Active
  },
  {
    id: 'dev-group-2',
    description: 'Advanced Leadership Cohort',
    status: GroupStatus.Active
  },
  {
    id: 'dev-group-3',
    description: 'Coach Development Circle',
    status: GroupStatus.Active
  },
  {
    id: 'dev-group-4',
    description: 'Coach-Led Growth Cohort',
    status: GroupStatus.Active
  },
  {
    id: 'dev-group-complete',
    description: 'Completed Leadership Cohort',
    status: GroupStatus.Active
  },
  {
    id: 'dev-group-complete-2',
    description: 'Emerging Leaders Accelerator',
    status: GroupStatus.Active
  }
];

// --- Sessions ---

export const baseSessions: ICoachingSessionFullDTO[] = [
  {
    id: 'dev-session-passed',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: 'This session occurred in the past but was never marked complete.',
    duration: 60,
    date: getDateOffset(-1, 14),
    topic: 'Passed Session (Confirmed)',
    createdAt: getDateOffset(-14, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo]
    }
  },
  {
    id: 'dev-session-group2',
    groupId: 'dev-group-2',
    group: {
      id: 'dev-group-2',
      description: 'Advanced Leadership Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-30, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Individual,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 60,
    date: getDateOffset(4, 10),
    topic: 'Personal Goal Setting',
    createdAt: getDateOffset(-2, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: currentSessionUser.id,
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-2',
      description: 'Advanced Leadership Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-30, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      participants: [currentSessionUser]
    }
  },
  {
    id: 'dev-session-coach-participant',
    groupId: 'dev-group-3',
    group: {
      id: 'dev-group-3',
      description: 'Coach Development Circle',
      leaderId: leaderUser.id,
      participantIds: `${coLeaderUser.id},${coachCohortPeer.id},${coachCohortPeerTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-45, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: 'Casey is attending this coach development session, not leading it.',
    duration: 60,
    date: getDateOffset(6, 13),
    topic: 'Coach Practice Lab',
    createdAt: getDateOffset(-12, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-3',
      description: 'Coach Development Circle',
      leaderId: leaderUser.id,
      participantIds: `${coLeaderUser.id},${coachCohortPeer.id},${coachCohortPeerTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-45, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      participants: [coLeaderUser, coachCohortPeer, coachCohortPeerTwo]
    }
  },
  {
    id: 'dev-session-coach-led-cohort',
    groupId: 'dev-group-4',
    group: {
      id: 'dev-group-4',
      description: 'Coach-Led Growth Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${coachLedParticipant.id},${coachLedParticipantTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: 'Casey leads this separate cohort to exercise multi-cohort coach previews.',
    duration: 60,
    date: getDateOffset(10, 12),
    topic: 'Scaling Trust Across Teams',
    createdAt: getDateOffset(-18, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-4',
      description: 'Coach-Led Growth Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${coachLedParticipant.id},${coachLedParticipantTwo.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: coLeaderUser,
      participants: [coachLedParticipant, coachLedParticipantTwo]
    }
  },
  {
    id: 'dev-session-today',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${cohortPeerThree.id},${cohortPeerFour.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 60,
    date: getDateOffsetRelative(20),
    topic: 'Executive Presence Sprint',
    createdAt: getDateOffset(-14, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${cohortPeerThree.id},${cohortPeerFour.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo, cohortPeerThree, cohortPeerFour]
    }
  },
  {
    id: 'dev-session-today-2',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Individual,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 45,
    date: getDateOffsetRelative(50),
    topic: 'Strategy Alignment Review',
    createdAt: getDateOffset(-5, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: currentSessionUser.id,
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      participants: [currentSessionUser]
    }
  },
  {
    id: 'dev-session-today-3',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Individual,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 30,
    date: getDateOffsetRelative(90),
    topic: 'Continuous Skills Q&A',
    createdAt: getDateOffset(-5, 9),
    updatedAt: getDateOffset(-1, 9),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: currentSessionUser.id,
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: coLeaderUser,
      participants: [currentSessionUser]
    }
  },
  {
    id: 'dev-session-next',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Individual,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 45,
    date: getDateOffset(3, 11, 30),
    topic: 'Goal Check-in',
    createdAt: getDateOffset(-10, 8),
    updatedAt: getDateOffset(-1, 8),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: currentSessionUser.id,
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      participants: [currentSessionUser]
    }
  },
  {
    id: 'dev-session-complete',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.RecurringSession,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-7, 13),
    topic: 'Feedback and Reflection',
    createdAt: getDateOffset(-21, 9),
    updatedAt: getDateOffset(-7, 14),
    url: '',
    completeUserIds: currentSessionUser.id,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-cancelled',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Cancelled,
    note: 'Coach had a conflict.',
    duration: 60,
    date: getDateOffset(-2, 10),
    topic: 'Cancelled Session',
    createdAt: getDateOffset(-10, 8),
    updatedAt: getDateOffset(-2, 8),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-future',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 60,
    date: getDateOffset(14, 15),
    topic: 'Navigating Change',
    createdAt: getDateOffset(-10, 8),
    updatedAt: getDateOffset(-1, 8),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-upcoming-1',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Individual,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 50,
    date: getDateOffset(5, 14),
    topic: 'Career Roadmap Review',
    createdAt: getDateOffset(-10, 8),
    updatedAt: getDateOffset(-1, 8),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: currentSessionUser.id,
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: coLeaderUser,
      participants: [currentSessionUser]
    }
  },
  {
    id: 'dev-session-upcoming-2',
    groupId: 'dev-group-1',
    group: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Confirmed,
    note: '',
    duration: 60,
    date: getDateOffset(8, 10),
    topic: 'Effective Delegation',
    createdAt: getDateOffset(-10, 8),
    updatedAt: getDateOffset(-1, 8),
    url: '',
    completeUserIds: '',
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-1',
      description: 'Dev Preview Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-90, 9),
      updatedAt: getDateOffset(-1, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-complete-cohort-1',
    groupId: 'dev-group-complete',
    group: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-42, 11),
    topic: 'Cohort Kickoff',
    createdAt: getDateOffset(-60, 9),
    updatedAt: getDateOffset(-42, 12),
    url: '',
    completeUserIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo, veteranValUser]
    }
  },
  {
    id: 'dev-session-complete-cohort-2',
    groupId: 'dev-group-complete',
    group: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-28, 11),
    topic: 'Influence Without Authority',
    createdAt: getDateOffset(-60, 9),
    updatedAt: getDateOffset(-28, 12),
    url: '',
    completeUserIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo, veteranValUser]
    }
  },
  {
    id: 'dev-session-complete-cohort-3',
    groupId: 'dev-group-complete',
    group: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-14, 13),
    topic: 'Difficult Conversations',
    createdAt: getDateOffset(-60, 9),
    updatedAt: getDateOffset(-14, 14),
    url: '',
    completeUserIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo, veteranValUser]
    }
  },
  // --- Emerging Leaders Accelerator (dev-group-complete-2) — Val's earlier completed cohort ---
  {
    id: 'dev-session-g2c-1',
    groupId: 'dev-group-complete-2',
    group: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-120, 10),
    topic: 'Program Kickoff',
    createdAt: getDateOffset(-150, 9),
    updatedAt: getDateOffset(-120, 11),
    url: '',
    completeUserIds: `${veteranValUser.id},${cohortPeer.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10),
      leader: leaderUser,
      participants: [veteranValUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-g2c-2',
    groupId: 'dev-group-complete-2',
    group: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-105, 14),
    topic: 'Communication Styles',
    createdAt: getDateOffset(-150, 9),
    updatedAt: getDateOffset(-105, 15),
    url: '',
    completeUserIds: `${veteranValUser.id},${cohortPeer.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10),
      leader: leaderUser,
      participants: [veteranValUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-g2c-3',
    groupId: 'dev-group-complete-2',
    group: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-90, 11),
    topic: 'Wrap Up',
    createdAt: getDateOffset(-150, 9),
    updatedAt: getDateOffset(-90, 12),
    url: '',
    completeUserIds: `${veteranValUser.id},${cohortPeer.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete-2',
      description: 'Emerging Leaders Accelerator',
      leaderId: leaderUser.id,
      participantIds: `${veteranValUser.id},${cohortPeer.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-150, 9),
      updatedAt: getDateOffset(-90, 10),
      leader: leaderUser,
      participants: [veteranValUser, cohortPeer]
    }
  },
  {
    id: 'dev-session-complete-cohort-final',
    groupId: 'dev-group-complete',
    group: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10)
    },
    stage: SessionStageType.Coaching,
    status: CoachingSessionStatus.Completed,
    note: '',
    duration: 60,
    date: getDateOffset(-3, 15),
    topic: 'Final Reflection',
    createdAt: getDateOffset(-30, 9),
    updatedAt: getDateOffset(-3, 16),
    url: '',
    completeUserIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
    linkedFileId: '',
    linkedTaskId: '',
    participantId: '',
    externalMeetingLink: '',
    groupDto: {
      id: 'dev-group-complete',
      description: 'Completed Leadership Cohort',
      leaderId: leaderUser.id,
      coLeaderId: coLeaderUser.id,
      participantIds: `${currentSessionUser.id},${cohortPeer.id},${cohortPeerTwo.id},${veteranValUser.id}`,
      status: GroupStatus.Active,
      createdAt: getDateOffset(-60, 9),
      updatedAt: getDateOffset(-3, 10),
      leader: leaderUser,
      coLeader: coLeaderUser,
      participants: [currentSessionUser, cohortPeer, cohortPeerTwo, veteranValUser]
    }
  }
];
